import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { StandardTitle } from './StandardTitle';

const styles = StyleSheet.create({
    container: {
        marginBottom: 10,
    },
    palletHeader: {
        fontSize: 14,
        color: '#004899',
        fontWeight: 'bold',
        marginBottom: 15,
        backgroundColor: '#f0f5ff',
        padding: 8,
        borderRadius: 4,
        textAlign: 'center',
    },
    noDataText: {
        fontSize: 10,
        color: '#666',
        fontStyle: 'italic',
        textAlign: 'center',
        marginBottom: 10,
    },
    rqlInfoContainer: {
        marginBottom: 15,
        backgroundColor: '#ffffff',
        borderRadius: 6,
        padding: 10,
        borderWidth: 1,
        borderColor: '#e0e0e0',
    },
    infoRow: {
        flexDirection: 'row',
        marginBottom: 8,
    },
    infoColumn: {
        width: '50%',
        paddingRight: 5,
    },
    infoField: {
        marginBottom: 8,
    },
    infoLabel: {
        fontSize: 9,
        color: '#666',
        marginBottom: 2,
        fontWeight: 'bold',
    },
    infoValue: {
        fontSize: 9,
        color: '#333',
        backgroundColor: '#f7f7f7',
        padding: 4,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: '#e0e0e0',
    },
    notesField: {
        marginBottom: 8,
    },
    notesValue: {
        fontSize: 9,
        color: '#333',
        backgroundColor: '#f7f7f7',
        padding: 4,
        borderRadius: 4,
        minHeight: 20,
        borderWidth: 1,
        borderColor: '#e0e0e0',
    },
    packageTypeHeader: {
        fontSize: 12,
        color: '#004899',
        fontWeight: 'bold',
        marginTop: 12,
        marginBottom: 5,
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
        paddingBottom: 3,
    },
    defectsTable: {
        width: '100%',
        marginBottom: 10,
        borderWidth: 1,
        borderColor: '#e0e0e0',
        borderRadius: 4,
        overflow: 'hidden',
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#f0f5ff',
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
    },
    tableHeaderCell: {
        padding: 4,
        fontSize: 8,
        fontWeight: 'bold',
        color: '#004899',
        borderRightWidth: 1,
        borderRightColor: '#e0e0e0',
        textAlign: 'center',
    },
    defectNameCell: {
        width: '35%',
        textAlign: 'left',
    },
    defectTypeCell: {
        width: '16.25%',
    },
    thresholdRow: {
        flexDirection: 'row',
        backgroundColor: '#f0f5ff',
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
    },
    thresholdCell: {
        padding: 4,
        fontSize: 8,
        fontWeight: 'bold',
        color: '#ef4444',
        borderRightWidth: 1,
        borderRightColor: '#e0e0e0',
        textAlign: 'center',
        width: '16.25%',
    },
    thresholdLabelCell: {
        padding: 4,
        fontSize: 8,
        fontWeight: 'bold',
        color: '#004899',
        borderRightWidth: 1,
        borderRightColor: '#e0e0e0',
        textAlign: 'left',
        width: '35%',
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
        minHeight: 22,
        alignItems: 'center',
    },
    tableCell: {
        padding: 4,
        fontSize: 8,
        color: '#333',
        borderRightWidth: 1,
        borderRightColor: '#e0e0e0',
        textAlign: 'center',
        width: '16.25%',
    },
    lastCell: {
        borderRightWidth: 0,
    },
    disabledCell: {
        backgroundColor: '#e9e9e9',
    },
    summaryTable: {
        marginTop: 10,
        marginBottom: 10,
        backgroundColor: '#f9f9f9',
        padding: 6,
        borderWidth: 1,
        borderColor: '#e0e0e0',
        borderRadius: 4,
    },
    summaryRow: {
        flexDirection: 'row',
        marginBottom: 3,
    },
    summaryLabel: {
        width: '60%',
        fontSize: 8,
        fontWeight: 'bold',
        color: '#004899',
    },
    summaryValue: {
        width: '40%',
        fontSize: 8,
    },
    passLabel: {
        color: '#22c55e',
        fontWeight: 'bold',
    },
    failLabel: {
        color: '#ef4444',
        fontWeight: 'bold',
    },
    totalDefectsHeader: {
        fontSize: 10,
        color: '#333',
        fontWeight: 'bold',
        marginTop: 8,
        marginBottom: 5,
    },
    sectionDivider: {
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
        marginVertical: 10,
    },
    defectTypeSummaryTable: {
        width: '100%',
        marginTop: 8,
        marginBottom: 16,
        borderWidth: 1,
        borderColor: '#e0e0e0',
        borderRadius: 4,
        overflow: 'hidden',
    },
    summaryTableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
    },
    lastSummaryRow: {
        borderBottomWidth: 0,
    },
    summaryLabelCell: {
        width: '35%',
        padding: 5,
        backgroundColor: '#f0f5ff',
        borderRightWidth: 1,
        borderRightColor: '#e0e0e0',
        fontSize: 8,
        fontWeight: 'bold',
        color: '#004899',
    },
    summaryDefectTypeCell: {
        width: '16.25%',
        padding: 5,
        textAlign: 'center',
        fontSize: 8,
        color: '#333',
        borderRightWidth: 1,
        borderRightColor: '#e0e0e0',
    },
    lastSummaryCell: {
        borderRightWidth: 0,
    },
    passCell: {
        backgroundColor: '#22c55e',
        color: 'white',
    },
    failCell: {
        backgroundColor: '#ef4444',
        color: 'white',
    }
});

// Define the thresholds for each defect type (same as in Rql.tsx)
const DEFECT_THRESHOLDS: { [key: string]: number } = {
    'critical': 0,
    'major1': 0.5,
    'major2': 2.9,
    'minor': 7.5
};

interface RQLDefect {
    id?: number;
    rqlId?: number;
    defectKeyId: number;
    defectType: 'critical' | 'major1' | 'major2' | 'minor';
    defectPercentage: string;
    defectKey?: {
        id: number;
        name: string;
        defectType: string;
        packageType: string;
    };
}

interface RQL {
    id?: number;
    palletId: number;
    variant: string;
    targetSampleSize: string;
    actualSampleSize: string;
    dateOfInspection: string;
    packageType: string;
    inspectedBy: string;
    totalDefects: number | null;
    percentDefects: number | null;
    passFail: string | null;
    criticalTotalDefects?: number | null;
    criticalPercentageDefects?: number | null;
    criticalPassFail?: string | null;
    major1TotalDefects?: number | null;
    major1PercentageDefects?: number | null;
    major1PassFail?: string | null;
    major2TotalDefects?: number | null;
    major2PercentageDefects?: number | null;
    major2PassFail?: string | null;
    minorTotalDefects?: number | null;
    minorPercentageDefects?: number | null;
    minorPassFail?: string | null;
    unclassifiedDefects?: string;
    rqlNotes?: string;
    defects: RQLDefect[];
}

interface Pallet {
    id: number;
    variant: string;
    name: string;
    rqls?: RQL[];
}

interface PreshipRQLSectionProps {
    pallets: Pallet[];
    variant?: 'preShip' | 'postShip' | 'postStack';
}

const RQL_TITLES = {
    preShip: 'PreShip RQL',
    postShip: 'PostShip RQL',
    postStack: 'PostStack RQL',
} as const;

export const PreshipRQLSection: React.FC<PreshipRQLSectionProps> = ({ pallets, variant = 'preShip' }) => {
    const formatPassFail = (value: string | null): string => {
        if (!value) return 'N/A';
        return value.toLowerCase() === 'pass' ? 'Pass' : 'Fail';
    };

    // Group defects by packageType for a given RQL
    const getDefectsByPackageType = (rql: RQL) => {
        const defectsByType: Record<string, RQLDefect[]> = {};

        if (rql.defects && rql.defects.length > 0) {
            rql.defects.forEach(defect => {
                const packageType = defect.defectKey?.packageType || 'unknown';
                if (!defectsByType[packageType]) {
                    defectsByType[packageType] = [];
                }
                defectsByType[packageType].push(defect);
            });
        }

        return defectsByType;
    };

    // Calculate defect summary for a specific package type - following the Rql.tsx logic
    const calculateDefectSummary = (rql: RQL, packageType: string) => {
        const summary: { [key: string]: { total: number, percentage: number, passFail: string } } = {
            critical: { total: 0, percentage: 0, passFail: 'Pass' },
            major1: { total: 0, percentage: 0, passFail: 'Pass' },
            major2: { total: 0, percentage: 0, passFail: 'Pass' },
            minor: { total: 0, percentage: 0, passFail: 'Pass' },
        };

        // Sum up defects by type
        if (rql.defects && rql.defects.length > 0) {
            rql.defects.forEach(defect => {
                if (defect.defectKey?.packageType === packageType && defect.defectPercentage) {
                    const defectCount = Number(defect.defectPercentage);
                    if (!isNaN(defectCount) && defect.defectType in summary) {
                        summary[defect.defectType].total += defectCount;
                    }
                }
            });
        }

        // Calculate percentages and determine pass/fail
        const actualSampleSize = Number(rql.actualSampleSize) || 100;
        Object.keys(summary).forEach(defectType => {
            const total = summary[defectType].total;
            const percentage = (total / actualSampleSize) * 100;
            summary[defectType].percentage = parseFloat(percentage.toFixed(2));
            summary[defectType].passFail = percentage > DEFECT_THRESHOLDS[defectType] ? 'Fail' : 'Pass';
        });

        return summary;
    };

    const getOverallPassFail = (rql: RQL): string => {
        if (rql.passFail) return formatPassFail(rql.passFail);

        const savedStatuses = [
            rql.criticalPassFail,
            rql.major1PassFail,
            rql.major2PassFail,
            rql.minorPassFail,
        ].filter((status): status is string => Boolean(status));

        if (savedStatuses.some(status => status.toLowerCase() === 'fail')) return 'Fail';
        if (savedStatuses.length > 0) return 'Pass';

        const packageTypes = Object.keys(getDefectsByPackageType(rql));
        if (packageTypes.some(packageType => (
            Object.values(calculateDefectSummary(rql, packageType)).some(summary => summary.passFail === 'Fail')
        ))) return 'Fail';

        if (rql.totalDefects !== null && rql.totalDefects !== undefined) return 'Pass';
        return 'N/A';
    };

    // Format date to a readable string
    const formatDate = (dateString: string): string => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch (error) {
            return dateString;
        }
    };

    const hasRQLData = pallets.some(pallet =>
        pallet.rqls?.some(rql => rql.variant === variant)
    );

    if (!hasRQLData) {
        return (
            <View style={styles.container}>
                <Text style={styles.noDataText}>No {RQL_TITLES[variant]} data available</Text>
            </View>
        );
    }

    // Get the defect type for a given defect key ID
    const getDefectTypeForKey = (rql: RQL, defectKeyId: number): string => {
        const defect = rql.defects.find(d => d.defectKeyId === defectKeyId);
        return defect?.defectType || '';
    };

    // Get all unique defect keys across all package types
    const getUniqueDefectKeys = (rql: RQL, packageType: string): any[] => {
        const keys: any[] = [];

        if (rql.defects && rql.defects.length > 0) {
            rql.defects.forEach(defect => {
                if (defect.defectKey && defect.defectKey.packageType === packageType) {
                    const exists = keys.some(k => k.id === defect.defectKey?.id);
                    if (!exists && defect.defectKey) {
                        keys.push({
                            id: defect.defectKey.id,
                            name: defect.defectKey.name,
                            defectType: defect.defectType,
                            packageType: defect.defectKey.packageType
                        });
                    }
                }
            });
        }

        return keys;
    };

    // Get the defect count for a specific defect key and type
    const getDefectCount = (rql: RQL, defectKeyId: number, defectType: string): string => {
        const defect = rql.defects.find(d => d.defectKeyId === defectKeyId && d.defectType === defectType);
        return defect ? defect.defectPercentage : '';
    };

    return (
        <View style={styles.container}>
            {pallets.map((pallet, palletIndex) => {
                const preshipRQL = pallet.rqls?.find(rql => rql.variant === variant);

                if (!preshipRQL) return null;

                // Get defects by package type for the single RQL entry
                const defectsByPackageType = getDefectsByPackageType(preshipRQL);

                const overallPassFail = getOverallPassFail(preshipRQL);

                return (
                    <View key={`pallet-rql-${palletIndex}`}>
                        <StandardTitle title={`${RQL_TITLES[variant]} - ${pallet.variant} - ${pallet.name}`} />

                        <View style={styles.rqlInfoContainer}>
                            {/* Basic Info Section - 2x3 Grid */}
                            <View>
                                <View style={styles.infoRow}>
                                    <View style={styles.infoColumn}>
                                        <View style={styles.infoField}>
                                            <Text style={styles.infoLabel}>Target Sample Size</Text>
                                            <Text style={styles.infoValue}>{preshipRQL.targetSampleSize || 'N/A'}</Text>
                                        </View>
                                    </View>
                                    <View style={styles.infoColumn}>
                                        <View style={styles.infoField}>
                                            <Text style={styles.infoLabel}>Actual Sample Size</Text>
                                            <Text style={styles.infoValue}>{preshipRQL.actualSampleSize || 'N/A'}</Text>
                                        </View>
                                    </View>
                                </View>

                                <View style={styles.infoRow}>
                                    <View style={styles.infoColumn}>
                                        <View style={styles.infoField}>
                                            <Text style={styles.infoLabel}>Date of Inspection</Text>
                                            <Text style={styles.infoValue}>{formatDate(preshipRQL.dateOfInspection)}</Text>
                                        </View>
                                    </View>
                                    <View style={styles.infoColumn}>
                                        <View style={styles.infoField}>
                                            <Text style={styles.infoLabel}>Package Type</Text>
                                            <Text style={styles.infoValue}>
                                                {preshipRQL.packageType ? preshipRQL.packageType.charAt(0).toUpperCase() + preshipRQL.packageType.slice(1) : 'N/A'}
                                            </Text>
                                        </View>
                                    </View>
                                </View>

                                <View style={styles.infoRow}>
                                    <View style={styles.infoColumn}>
                                        <View style={styles.infoField}>
                                            <Text style={styles.infoLabel}>Inspected By</Text>
                                            <Text style={styles.infoValue}>{preshipRQL.inspectedBy || 'N/A'}</Text>
                                        </View>
                                    </View>
                                    <View style={styles.infoColumn}>
                                        <View style={styles.infoField}>
                                            <Text style={styles.infoLabel}>Overall Pass/Fail</Text>
                                            <Text style={styles.infoValue}>
                                                <Text style={overallPassFail === 'Pass' ? styles.passLabel : styles.failLabel}>
                                                    {overallPassFail}
                                                </Text>
                                            </Text>
                                        </View>
                                    </View>
                                </View>
                            </View>

                            {/* Notes Section */}
                            {preshipRQL.rqlNotes && (
                                <View style={styles.notesField}>
                                    <Text style={styles.infoLabel}>Notes</Text>
                                    <Text style={styles.notesValue}>{preshipRQL.rqlNotes}</Text>
                                </View>
                            )}

                            <View style={styles.sectionDivider} />

                            {/* Total Defects Summary */}
                            <Text style={styles.totalDefectsHeader}>Total Defects: {preshipRQL.totalDefects !== null ? preshipRQL.totalDefects : 'N/A'} ({preshipRQL.percentDefects !== null ? `${preshipRQL.percentDefects}%` : 'N/A'})</Text>

                            {/* Defects by Package Type */}
                            {Object.keys(defectsByPackageType).length > 0 && Object.entries(defectsByPackageType).map(([packageType, defects], packageIndex) => {
                                // Calculate the defect summary for this package type
                                const defectSummary = calculateDefectSummary(preshipRQL, packageType);
                                // Get unique defect keys for this package type
                                const uniqueDefectKeys = getUniqueDefectKeys(preshipRQL, packageType);

                                return (
                                    <View key={`package-${packageIndex}`} wrap={false}>
                                        <Text style={styles.packageTypeHeader}>
                                            {packageType.charAt(0).toUpperCase() + packageType.slice(1)} Defects
                                        </Text>

                                        <View style={styles.defectsTable}>
                                            {/* Table Header - First row */}
                                            <View style={styles.tableHeader}>
                                                <Text style={[styles.tableHeaderCell, styles.defectNameCell]}>
                                                    {packageType.charAt(0).toUpperCase() + packageType.slice(1)} Defects
                                                </Text>
                                                <Text style={[styles.tableHeaderCell, styles.defectTypeCell]}>Critical</Text>
                                                <Text style={[styles.tableHeaderCell, styles.defectTypeCell]}>Major 1</Text>
                                                <Text style={[styles.tableHeaderCell, styles.defectTypeCell]}>Major 2</Text>
                                                <Text style={[styles.tableHeaderCell, styles.defectTypeCell, styles.lastCell]}>Minor</Text>
                                            </View>

                                            {/* RQL Level row */}
                                            <View style={styles.thresholdRow}>
                                                <Text style={styles.thresholdLabelCell}>RQL Level</Text>
                                                <Text style={styles.thresholdCell}>0%</Text>
                                                <Text style={styles.thresholdCell}>0.5%</Text>
                                                <Text style={styles.thresholdCell}>2.9%</Text>
                                                <Text style={[styles.thresholdCell, styles.lastCell]}>7.5%</Text>
                                            </View>

                                            {/* Defect rows */}
                                            {uniqueDefectKeys.map((defectKey, defectKeyIndex) => {
                                                const defectType = defectKey.defectType;
                                                const isLastRow = defectKeyIndex === uniqueDefectKeys.length - 1;

                                                return (
                                                    <View
                                                        key={`defect-key-${defectKeyIndex}`}
                                                        style={[
                                                            styles.tableRow,
                                                            isLastRow ? styles.lastRow : {}
                                                        ]}
                                                    >
                                                        <Text style={[styles.tableCell, styles.defectNameCell]}>
                                                            {defectKey.name}
                                                        </Text>

                                                        {['critical', 'major1', 'major2', 'minor'].map((type, typeIndex) => {
                                                            const isActiveType = defectType === type;
                                                            const count = isActiveType ?
                                                                getDefectCount(preshipRQL, defectKey.id, type) : '';
                                                            const isLastCell = typeIndex === 3;

                                                            return (
                                                                <Text
                                                                    key={`defect-${defectKeyIndex}-${type}`}
                                                                    style={[
                                                                        styles.tableCell,
                                                                        !isActiveType ? styles.disabledCell : {},
                                                                        isLastCell ? styles.lastCell : {}
                                                                    ]}
                                                                >
                                                                    {count}
                                                                </Text>
                                                            );
                                                        })}
                                                    </View>
                                                );
                                            })}
                                        </View>

                                        {/* Add defect type summary table for this package type */}
                                        <View style={styles.defectTypeSummaryTable}>
                                            {/* Total Defects row */}
                                            <View style={styles.summaryTableRow}>
                                                <Text style={styles.summaryLabelCell}>Total Defects</Text>
                                                {['critical', 'major1', 'major2', 'minor'].map((defectType, index) => (
                                                    <Text
                                                        key={`${packageType}-${defectType}-total`}
                                                        style={[
                                                            styles.summaryDefectTypeCell,
                                                            index === 3 ? styles.lastSummaryCell : {}
                                                        ]}
                                                    >
                                                        {defectSummary[defectType].total}
                                                    </Text>
                                                ))}
                                            </View>

                                            {/* Percentage Defects row */}
                                            <View style={styles.summaryTableRow}>
                                                <Text style={styles.summaryLabelCell}>Percentage Defects</Text>
                                                {['critical', 'major1', 'major2', 'minor'].map((defectType, index) => (
                                                    <Text
                                                        key={`${packageType}-${defectType}-percentage`}
                                                        style={[
                                                            styles.summaryDefectTypeCell,
                                                            index === 3 ? styles.lastSummaryCell : {}
                                                        ]}
                                                    >
                                                        {defectSummary[defectType].percentage}%
                                                    </Text>
                                                ))}
                                            </View>

                                            {/* Pass/Fail row */}
                                            <View style={[styles.summaryTableRow, styles.lastSummaryRow]}>
                                                <Text style={styles.summaryLabelCell}>Pass/Fail</Text>
                                                {['critical', 'major1', 'major2', 'minor'].map((defectType, index) => {
                                                    const isPass = defectSummary[defectType].passFail === 'Pass';

                                                    return (
                                                        <Text
                                                            key={`${packageType}-${defectType}-passfail`}
                                                            style={[
                                                                styles.summaryDefectTypeCell,
                                                                isPass ? styles.passCell : styles.failCell,
                                                                index === 3 ? styles.lastSummaryCell : {}
                                                            ]}
                                                        >
                                                            {defectSummary[defectType].passFail}
                                                        </Text>
                                                    );
                                                })}
                                            </View>
                                        </View>
                                    </View>
                                );
                            })}

                            {Object.keys(defectsByPackageType).length === 0 && (
                                <Text style={styles.noDataText}>No defects recorded</Text>
                            )}
                        </View>
                    </View>
                );
            })}
        </View>
    );
};
